"use client";

import { useState } from "react";
import { AlertTriangle, CheckCircle2, ClipboardPaste, X } from "lucide-react";
import { importLessonJson, type ImportResult } from "./importJson";
import type { EditorSection } from "./blockDefaults";
import type { LessonContent } from "@/lib/lesson-content/schema";

type Props = {
  hasExistingContent: boolean;
  onImport: (sections: EditorSection[], intro?: LessonContent["intro"]) => void;
  onClose: () => void;
};

/**
 * Paste LLM-generated lesson JSON, validate it against the schema (IDs regenerated
 * automatically), and load it onto the canvas. Nothing is persisted here — the
 * author reviews in the live preview and clicks Save.
 */
export default function ImportJsonModal({ hasExistingContent, onImport, onClose }: Props) {
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);

  const validate = () => setResult(importLessonJson(raw));

  const doImport = () => {
    const r = result && result.ok ? result : importLessonJson(raw);
    setResult(r);
    if (!r.ok) return;
    if (
      hasExistingContent &&
      !window.confirm("This replaces the current lesson content on the canvas. You still have to Save afterward. Continue?")
    ) {
      return;
    }
    onImport(r.sections, r.intro);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <ClipboardPaste className="h-5 w-5 text-indigo-600" />
            <h2 className="text-base font-semibold text-slate-900">Import lesson JSON</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <p className="text-sm text-slate-500">
            Paste the JSON your LLM generated. It&apos;s validated against the schema and its section/block IDs are
            regenerated automatically. Nothing is saved until you import and then <span className="font-medium">Save</span>.
          </p>
          <textarea
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              if (result) setResult(null);
            }}
            placeholder={'{\n  "version": 1,\n  "format": "blocks",\n  "sections": [ … ]\n}'}
            spellCheck={false}
            className="h-64 w-full resize-y rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          {result && result.ok && (
            <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              Valid — {result.summary.sections} section{result.summary.sections === 1 ? "" : "s"}, {result.summary.blocks}{" "}
              block{result.summary.blocks === 1 ? "" : "s"}. Ready to import.
            </div>
          )}
          {result && !result.ok && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <div className="mb-1 flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {result.errors.length} problem{result.errors.length === 1 ? "" : "s"} — fix these or re-prompt:
              </div>
              <ul className="list-disc space-y-0.5 pl-5">
                {result.errors.slice(0, 30).map((e, i) => (
                  <li key={i} className="font-mono text-xs">
                    {e}
                  </li>
                ))}
              </ul>
              {result.errors.length > 30 && <p className="mt-1 text-xs">…and {result.errors.length - 30} more.</p>}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={validate}
            disabled={!raw.trim()}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Validate
          </button>
          <button
            onClick={doImport}
            disabled={!raw.trim()}
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Import to canvas
          </button>
        </div>
      </div>
    </div>
  );
}
